import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { addFact, BrochureReadError, downloadProjectFile, findUnprocessedBrochure, listFacts, readBrochure, readUploadedBrochure, retireFact, reviewFact, setMemberPhone, setProjectRegistration, setProjectTeam, startNewPriceSheet, writeProjectKit, type BrochureRetry } from '../lib/api';
import { dateOnly, factCategoryLabel, formatPhone, friendlyError } from '../lib/format';
import type { FactCategory, Project, ProjectFact } from '../lib/types';
import { Badge, Button, Empty, ErrorText, Modal, Notice, SelectField, TextField } from '../components/ui';
import { ProjectMessages } from '../components/ProjectMessages';

const CATEGORIES = Object.keys(factCategoryLabel) as FactCategory[];

export function ProjectDetail() {
  const { id = '' } = useParams();
  const { projects, firm, isManager, reload, teams } = useAuth();
  const project = projects.find((p) => p.id === id);
  const [facts, setFacts] = useState<ProjectFact[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reading, setReading] = useState('');
  const [retry, setRetry] = useState<BrochureRetry | null>(null);
  const [official, setOfficial] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newSheet, setNewSheet] = useState(false);

  const load = useCallback(async () => { try { setFacts(await listFacts(id)); } catch (e) { setError(friendlyError(e)); } }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isManager || !firm || !project) return;
    let cancelled = false;
    findUnprocessedBrochure(firm.id, project).then((saved) => {
      if (saved && !cancelled) { setRetry(saved); setOfficial(false); }
    }).catch((e) => {
      if (!cancelled) setError(`Could not check for an unfinished brochure upload. You may upload the file again. ${friendlyError(e)}`);
    });
    return () => { cancelled = true; };
  }, [firm?.id, isManager, project?.id, project?.brochure_path, project?.brochure_uploaded_at]);

  const drafts = facts.filter((f) => f.status === 'draft');
  const approved = facts.filter((f) => f.status === 'approved');
  const byCategory = useMemo(() => CATEGORIES.map((c) => [c, approved.filter((f) => f.category === c)] as const).filter(([, fs]) => fs.length), [approved]);
  const today = new Date().toISOString().slice(0, 10);
  const billingError = /(?:429.*(?:no credits|quota|balance)|no credits remaining|credit_balance_exhausted)/i.test(error);

  if (!project) return <Empty title="Project not found"><Link className="text-accent" to="/projects">Back to projects</Link></Empty>;

  async function completeRead(r: { count: number; retired: number; official: boolean }) {
    setRetry(null);
    await load(); await reload();
    if (r.official) {
      setReading('Writing a buyer-ready sales brief…');
      try { await writeProjectKit(project!.id); await reload(); }
      catch (e) { setError(`The facts were saved, but the sales brief failed: ${friendlyError(e)}. Use "Write with AI" to retry.`); }
      setNotice(`Published ${r.count} page-cited facts${r.retired ? `; ${r.retired} old facts were superseded` : ''}. Check them under "All facts" if needed.`);
    } else {
      setNotice(`${r.count} cited facts are waiting for manager approval. Nothing has been published to buyers.`);
    }
  }

  async function onFile(file: File | undefined) {
    if (!file || !firm) return;
    setRetry(null); setReading('Reading the brochure… long PDFs can take over a minute.'); setError(''); setNotice('');
    try {
      const r = await readBrochure(firm.id, project!.id, file, official);
      await completeRead(r);
    } catch (e) {
      if (e instanceof BrochureReadError) setRetry(e.retry);
      setError(`${friendlyError(e)}${e instanceof BrochureReadError ? ' Your uploaded file is saved. Retry reading it below; no new facts were published.' : ''}`);
    } finally { setReading(''); }
  }

  async function retryRead() {
    if (!retry) return;
    setReading('Retrying the saved brochure…'); setError(''); setNotice('');
    try { await completeRead(await readUploadedBrochure({ ...retry, official })); }
    catch (e) { setError(`${friendlyError(e)}. Your saved upload can be retried again, or you can upload a shorter document.`); }
    finally { setReading(''); }
  }

  async function act(fn: () => Promise<unknown>) {
    setError('');
    try { await fn(); await load(); await reload(); } catch (e) { setError(friendlyError(e)); }
  }

  const sheet = project.sheet;
  return (
    <div>
      <Link to="/projects" className="inline-flex items-center gap-2 text-sm font-semibold text-muted hover:text-accent">← All projects</Link>
      <div className="mb-6 mt-5 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-2"><span className="section-eyebrow">Project workspace</span><Badge tone={approved.length > 0 ? 'ok' : 'warn'}>{approved.length > 0 ? `${approved.length} approved facts` : 'Facts needed'}</Badge></div>
          <h1 className="text-3xl font-extrabold tracking-[-.035em] text-ink sm:text-[36px]">{project.name}</h1>
          <p className="mt-1 text-sm text-muted">{[project.builder, project.micro_market, project.city].filter(Boolean).join('  ·  ') || 'Project overview'}</p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium text-muted">
            {project.rera_number && <span>RERA {project.rera_number}</span>}
            {project.prices_as_of && <span>Facts recorded {dateOnly(project.prices_as_of)} · not a builder price confirmation</span>}
          </div>
        </div>
        {isManager && <div className="flex shrink-0 flex-wrap items-start gap-2">
          <label className={`inline-flex min-h-10 cursor-pointer items-center justify-center rounded-xl bg-accent px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-[#1b476c] ${reading ? 'pointer-events-none opacity-50' : ''}`}>
            {reading ? 'Reading brochure…' : project.brochure_path ? '↻ Replace brochure' : '+ Upload brochure'}
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="sr-only" disabled={!!reading} onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          <details className="group relative rounded-xl border border-line bg-white p-1 shadow-sm">
            <summary className="cursor-pointer list-none rounded-lg px-3 py-1.5 text-center text-sm font-semibold text-ink hover:bg-surface2">Options <span aria-hidden="true" className="ml-1 text-muted">⌄</span></summary>
            <div className="space-y-3 border-t border-line p-3 sm:absolute sm:right-0 sm:top-[48px] sm:z-30 sm:w-[290px] sm:rounded-2xl sm:border sm:bg-white sm:shadow-xl">
              <label className="flex items-start gap-2 text-xs leading-relaxed text-muted"><input type="checkbox" className="mt-0.5" checked={official} onChange={(e) => setOfficial(e.target.checked)} />Official builder document — publish extracted facts after reading</label>
              <p className="text-xs text-muted">Turn this off to send extracted facts for review before publishing.</p>
              <div className="grid grid-cols-2 gap-2"><Button onClick={() => setAdding(true)}>Add fact</Button><Button onClick={() => setNewSheet(true)}>New price sheet</Button></div>
            </div>
          </details>
        </div>}
      </div>

      {reading && <div className="mb-4"><Notice>{reading}</Notice></div>}
      {notice && <div className="mb-4"><Notice>{notice}</Notice></div>}
      {billingError ? <div role="alert" className="mb-4 flex flex-col gap-3 rounded-2xl border border-line bg-warnSoft px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
        <div><p className="font-bold text-ink">AI couldn't read this brochure yet</p><p className="text-muted">OpenAI reported insufficient API credits on the last attempt. If credits were just added, retry below. Your file is saved; no new facts were published.</p></div>
        <a href="https://platform.openai.com/settings/organization/billing/overview" target="_blank" rel="noreferrer" className="shrink-0 rounded-xl bg-white px-4 py-2 font-bold text-accent hover:underline">Check billing ↗</a>
      </div> : <div className="mb-4"><ErrorText>{error}</ErrorText></div>}
      {retry && !reading && isManager && <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-line bg-white p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
        <div><p className="font-bold text-ink">Unfinished brochure: {retry.fileName}</p>
          <p className="text-muted">Saved securely. Retry reading it without uploading again.</p>
          <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-muted"><input type="checkbox" checked={official} onChange={(e) => setOfficial(e.target.checked)} />This is the builder's official document — publish extracted facts after reading.</label>
          {!official && <p className="mt-1 text-xs text-muted">Extracted facts will wait for manager approval.</p>}
        </div>
        <Button variant="secondary" onClick={retryRead}>↻ Retry brochure reading</Button>
      </div>}

      {!project.brochure_path && approved.length === 0 && drafts.length === 0 && (
        <Empty title="No brochure yet">{isManager ? "Upload the builder's brochure. Everyone in the team sees the project sheet, talking points and a ready WhatsApp message straight away." : 'Your manager uploads the builder\'s brochure here.'}</Empty>
      )}

      {(project.kit || (isManager && approved.length > 0) || project.brochure_path) && (
        <div className="mb-5 grid items-start gap-5 lg:grid-cols-[minmax(0,1.75fr)_minmax(290px,.75fr)]">
          {(project.kit || (isManager && approved.length > 0)) && <KitCard projectId={project.id} onError={setError} />}
          {project.brochure_path && <BrochureCard projectId={project.id} />}
        </div>
      )}

      <details className="workspace-card overflow-hidden">
        <summary className="cursor-pointer px-5 py-4 text-sm font-bold text-ink hover:bg-surface2">
          Project details & supporting facts <span className="ml-2 font-normal text-muted">({approved.length} approved{drafts.length > 0 ? `, ${drafts.length} to check` : ''})</span>
        </summary>
      <div className="grid gap-4 border-t border-line p-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          {sheet && (sheet.key_facts.length > 0 || sheet.unit_rows.length > 0) && (
            <section className="rounded-2xl border border-line bg-surface p-4">
              {sheet.key_facts.length > 0 && (
                <dl className="mb-4 grid gap-x-6 gap-y-2 sm:grid-cols-2">
                  {sheet.key_facts.map((k, i) => {
                    const [a, ...b] = k.split(':');
                    return b.length ? <div key={i}><dt className="text-xs text-muted">{a.trim()}</dt><dd className="text-sm font-medium">{b.join(':').trim()}</dd></div>
                      : <div key={i} className="text-sm font-medium">{k}</div>;
                  })}
                </dl>
              )}
              {sheet.unit_rows.length > 0 && <UnitTable rows={sheet.unit_rows} />}
            </section>
          )}
          {sheet && (sheet.amenities.length > 0 || sheet.location.length > 0) && (
            <section className="grid gap-4 rounded-2xl border border-line bg-surface p-4 sm:grid-cols-2">
              {sheet.location.length > 0 && (
                <div>
                  <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Location</h2>
                  <ul className="flex flex-col gap-1 text-sm">{sheet.location.slice(0, 8).map((l, i) => <li key={i}>{l}</li>)}</ul>
                  {sheet.location.length > 8 && <details className="mt-2 text-sm"><summary className="cursor-pointer text-accent">{sheet.location.length - 8} more location details</summary>
                    <ul className="mt-2 flex flex-col gap-1">{sheet.location.slice(8).map((l, i) => <li key={i}>{l}</li>)}</ul></details>}
                </div>
              )}
              {sheet.amenities.length > 0 && (
                <div>
                  <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Amenities</h2>
                  <Amenities items={sheet.amenities} />
                </div>
              )}
            </section>
          )}

          {drafts.length > 0 && (
            <details className="rounded-2xl border border-warn bg-surface p-4" open={drafts.length <= 8 ? true : undefined}>
              <summary className="cursor-pointer font-medium">Facts to check ({drafts.length})</summary>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h2 className="text-sm text-muted">Only approved facts are used in buyer replies</h2>
                {isManager && <Button onClick={() => act(async () => { for (const d of drafts) await reviewFact(d.id, true); })}>Approve all</Button>}
              </div>
              <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-warn bg-surface">
                {drafts.map((f) => <DraftFact key={f.id} fact={f} canEdit={isManager} onDone={load} onError={setError} />)}
              </ul>
            </details>
          )}

          {approved.length > 0 && (
            <details className="rounded-2xl border border-line bg-surface">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
                All facts the AI desk may quote ({approved.length})
                <span className="block text-xs font-normal text-muted">The AI answers buyers with these only. A reply with any other number waits for review.</span>
              </summary>
              <div className="border-t border-line px-4 py-3">
                {byCategory.map(([cat, fs]) => (
                  <div key={cat} className="mb-3">
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted">{factCategoryLabel[cat]}</h3>
                    <ul className="flex flex-col">
                      {fs.map((f) => {
                        const expired = f.valid_until && f.valid_until < today;
                        return (
                          <li key={f.id} className="group grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-2 border-b border-line py-1.5 text-sm last:border-0">
                            <span className="min-w-0"><span className="text-muted">{f.label}: </span>{f.value}
                              <span className="text-xs text-muted">{f.source_page ? ` · p.${f.source_page}` : f.created_kind === 'member' ? ' · added by hand' : ''}</span>
                              {expired && <span className="ml-1"><Badge tone="bad">Ended</Badge></span>}
                            </span>
                            {isManager && <button className="text-xs text-muted hover:text-bad" onClick={() => act(() => retireFact(f.id))}>Remove</button>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          {isManager && teams.length > 0 && (
            <label className="flex flex-wrap items-center gap-2 rounded-2xl border border-line bg-surface px-4 py-3 text-sm">
              Team that handles this project:
              <select value={project.team_id ?? ''} onChange={(e) => act(() => setProjectTeam(project.id, e.target.value || null))}
                className="rounded-lg border border-line px-3 py-1.5">
                <option value="">Any team</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          )}
          {isManager && <RegistrationSettings key={project.id} projectId={project.id} email={project.builder_email} days={project.registration_valid_days}
            onSaved={async () => { await reload(); }} onError={setError} />}
        </div>
      </div>
      </details>

      {adding && <AddFactModal projectId={project.id} onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await load(); await reload(); }} />}
      {newSheet && <NewSheetModal projectId={project.id} onClose={() => setNewSheet(false)}
        onSaved={async (n) => { setNewSheet(false); setNotice(`${n} old price and payment-plan fact${n === 1 ? '' : 's'} retired. Replies waiting for review on this project are now stale. Upload the new sheet or add the new prices.`); await load(); await reload(); }} />}
    </div>
  );
}

function UnitTable({ rows }: { rows: NonNullable<Project['sheet']>['unit_rows'] }) {
  const [all, setAll] = useState(false);
  const cols = (['units', 'size', 'balcony', 'price', 'booking'] as const).filter((k) => rows.some((r) => r[k]));
  const head: Record<string, string> = { units: 'Units', size: 'Size', balcony: 'Balcony', price: 'Price', booking: 'Booking amount' };
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-sm">
        <thead className="text-left text-xs text-muted"><tr><th className="py-1 pr-3 font-medium">Unit</th>{cols.map((c) => <th key={c} className="py-1 pr-3 font-medium">{head[c]}</th>)}</tr></thead>
        <tbody className="divide-y divide-line">
          {(all ? rows : rows.slice(0, 8)).map((r, i) => <tr key={i} className="align-top"><td className="py-2 pr-3 font-medium">{r.type}</td>{cols.map((c) => <td key={c} className="py-2 pr-3">{r[c] ?? '—'}</td>)}</tr>)}
        </tbody>
      </table>
      {rows.length > 8 && <button className="mt-2 text-sm font-medium text-accent hover:underline" onClick={() => setAll(!all)}>{all ? 'Show fewer units' : `Show all ${rows.length} unit types`}</button>}
    </div>
  );
}

function BrochureCard({ projectId }: { projectId: string }) {
  const { projects, firm, me, reload, session } = useAuth();
  const p = projects.find((x) => x.id === projectId)!;
  const [busy, setBusy] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const isPdf = (p.brochure_path ?? '').toLowerCase().endsWith('.pdf');
  const save = (blob: Blob, name: string) => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 5000); };
  const fileBase = (p.brochure_name ?? `${p.name}.pdf`).replace(/\.[a-z]+$/i, '');

  async function original() {
    setBusy('open'); setError('');
    try { save(await downloadProjectFile(p.brochure_path!), p.brochure_name ?? 'brochure.pdf'); } catch (e) { setError(friendlyError(e)); } finally { setBusy(''); }
  }
  async function branded() {
    if (!me || !firm) return;
    setBusy('brand'); setError('');
    try {
      if (!me.phone && phone) { await setMemberPhone(me.id, phone); await reload(); }
      const myPhone = me.phone ?? (phone ? phone : null);
      const blob = await downloadProjectFile(p.brochure_path!);
      const { brandBrochure } = await import('../lib/brand');
      const out = await brandBrochure(await blob.arrayBuffer(), {
        firm: firm.name, firmRera: firm.rera_number, rep: me.full_name, phone: myPhone ? formatPhone(myPhone.startsWith('+') ? myPhone : `+91${myPhone.replace(/\D/g, '').slice(-10)}`) : null,
        email: session?.user.email ?? null, project: p.name, projectRera: p.rera_number, builder: p.builder,
      });
      save(new Blob([out], { type: 'application/pdf' }), `${fileBase} - ${firm.name} - ${me.full_name}.pdf`);
    } catch (e) { setError(friendlyError(e)); } finally { setBusy(''); }
  }

  return <section className="workspace-card p-5">
    <div className="mb-5 flex items-center gap-3">
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accentSoft text-lg text-accent" aria-hidden="true">▤</div>
      <div className="min-w-0"><p className="section-eyebrow">Project document</p><h2 className="text-base font-extrabold">Builder brochure</h2></div>
    </div>
    <div className="rounded-xl border border-line bg-surface2 px-3.5 py-3">
      <p className="truncate text-sm font-semibold">{p.brochure_name ?? 'Builder brochure'}</p>
      {p.brochure_uploaded_at && <p className="mt-0.5 text-xs text-muted">Uploaded {dateOnly(p.brochure_uploaded_at)}</p>}
    </div>
    <p className="mt-4 text-sm leading-relaxed text-muted">Share a brochure carrying your name and the firm's RERA information.</p>
    {isPdf && !me?.phone && <details className="mt-3 rounded-xl border border-line p-3">
      <summary className="cursor-pointer text-xs font-bold text-accent">Add your mobile to the brochure (optional)</summary>
      <div className="mt-3"><TextField label="Mobile number" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98110 22334" hint="Saved to your rep profile." /></div>
    </details>}
    <div className="mt-4 flex flex-col gap-2">
      {isPdf && <Button variant="primary" className="w-full" disabled={!!busy} onClick={branded}>{busy === 'brand' ? 'Preparing your copy…' : '↓ Download branded brochure'}</Button>}
      <Button variant="secondary" className="w-full" disabled={!!busy} onClick={original}>{busy === 'open' ? 'Downloading…' : 'Download original PDF'}</Button>
    </div>
    {isPdf && <details className="mt-4 text-xs leading-relaxed text-muted"><summary className="cursor-pointer font-semibold">What is added to my copy?</summary><p className="mt-2">A cover and footer with your details. The original builder pages are preserved.</p></details>}
    <div className="mt-3"><ErrorText>{error}</ErrorText></div>
  </section>;
}

function KitCard({ projectId, onError }: { projectId: string; onError: (m: string) => void }) {
  const { projects, isManager, reload, me } = useAuth();
  const p = projects.find((x) => x.id === projectId)!;
  const kit = p.kit?.version === 2 ? p.kit : null;
  const [busy, setBusy] = useState(false);
  const [allPrices, setAllPrices] = useState(false);

  async function write() {
    setBusy(true);
    try { await writeProjectKit(projectId); await reload(); } catch (e) { onError(friendlyError(e)); } finally { setBusy(false); }
  }
  const reraMissing = kit?.reasons.some((r) => /firm has no agent RERA/.test(r));

  return (
    <section className="workspace-card overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-accentSoft text-xl font-bold text-accent" aria-hidden="true">✦</div>
          <div><p className="section-eyebrow">ReAstra intelligence</p><h2 className="text-lg font-extrabold tracking-tight">Sales brief</h2></div>
        </div>
        <div className="flex items-center gap-2">
          {kit && <Badge tone={kit.lane === 'green' ? 'ok' : kit.lane === 'red' ? 'bad' : 'warn'}>{kit.lane === 'green' ? 'Ready to share' : 'Needs review'}</Badge>}
          {isManager && <Button variant="secondary" disabled={busy} onClick={write}>{busy ? 'Writing…' : kit ? 'Regenerate' : 'Generate brief'}</Button>}
        </div>
      </header>
      <div className="p-5 sm:p-6">
        {!kit && <p className="text-sm text-muted">{isManager ? 'Generate a buyer-first pitch, clear prices and three WhatsApp messages from the approved project facts.' : 'Your manager will prepare the sales brief from the builder’s brochure.'}</p>}
        {kit && <>
          {kit.lane !== 'green' && <div className="mb-5 rounded-xl border border-line bg-surface2 px-4 py-3 text-sm">
            <p className="font-bold text-ink">Not cleared for sharing yet</p>
            <p className="mt-1 text-muted">{reraMissing
              ? <>Your firm's RERA agent number is required. {me?.role === 'owner' ? <Link className="font-bold text-accent underline" to="/settings">Add it in Settings → Firm details</Link> : 'Ask the owner to add it in Settings → Firm details'}, then regenerate.</>
              : 'A manager needs to check the source facts before anyone sends the messages below.'}</p>
            {!reraMissing && <details className="mt-2 text-xs text-muted"><summary className="cursor-pointer font-semibold text-accent">See what needs checking</summary><p className="mt-1">{kit.reasons.join(' · ')}</p></details>}
          </div>}

          <p className="section-eyebrow">What to say first</p>
          <p className="mt-2 max-w-[65ch] text-[19px] font-bold leading-[1.42] tracking-[-.015em] text-ink sm:text-[21px]">{kit.pitch}</p>

          {(kit.price_ladder?.length ?? 0) > 0 && <div className="mt-7">
            <div className="mb-3 flex items-center justify-between"><h3 className="text-sm font-extrabold">Prices at a glance</h3><span className="text-xs text-muted">As listed in approved facts</span></div>
            <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
              {(allPrices ? kit.price_ladder! : kit.price_ladder!.slice(0, 8)).map((r, i) => <div key={i} className="min-w-0 rounded-xl border border-line bg-surface2 p-3.5">
                <p className="text-xs font-bold text-muted">{r.unit}</p>
                <p className="mt-1 text-xl font-extrabold tracking-tight text-ink">{r.price}</p>
                {r.size && <p className="mt-1 text-xs font-medium text-muted">{r.size}</p>}
                {r.note && <p className="mt-2 border-t border-line pt-2 text-xs leading-relaxed text-muted">{r.note}</p>}
              </div>)}
            </div>
            {kit.price_ladder!.length > 8 && <button className="mt-3 text-sm font-bold text-accent hover:underline" onClick={() => setAllPrices(!allPrices)}>{allPrices ? 'Show fewer prices' : `Show all ${kit.price_ladder!.length} configurations`}</button>}
          </div>}

          {(kit.messages?.length ?? 0) > 0 && <div className="mt-7 border-t border-line pt-5"><ProjectMessages messages={kit.messages!} footer={kit.footer} lane={kit.lane} /></div>}
          {((kit.why_visit?.length ?? 0) + (kit.suits?.length ?? 0) + (kit.objections?.length ?? 0)) > 0 && <details className="mt-6 rounded-xl border border-line">
            <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-ink">Talking points & buyer questions</summary>
            <div className="border-t border-line p-4">
              {(kit.why_visit?.length ?? 0) > 0 && <Block title="Why visit"><ul className="flex list-disc flex-col gap-1 pl-5 text-sm">{kit.why_visit!.map((x, i) => <li key={i}>{x}</li>)}</ul></Block>}
              {(kit.suits?.length ?? 0) > 0 && <Block title="Best for"><div className="flex flex-wrap gap-1.5">{kit.suits!.map((x, i) => <span key={i} className="rounded-full bg-accentSoft px-2.5 py-1 text-xs text-accent">{x}</span>)}</div></Block>}
              {(kit.objections?.length ?? 0) > 0 && <Block title="If the buyer asks"><ul className="flex flex-col gap-2 text-sm">{kit.objections!.map((o, i) => <li key={i}><p className="font-bold">{o.q}</p><p className="text-muted">{o.a}</p></li>)}</ul></Block>}
            </div>
          </details>}
          <p className="mt-5 text-[11px] leading-relaxed text-muted">Generated {dateOnly(kit.written_at)} from approved facts. Confirm current availability and pricing with the builder before advising a buyer.</p>
        </>}
      </div>
    </section>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return <div className="mb-4"><h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted">{title}</h3>{children}</div>;
}

function DraftFact({ fact, canEdit, onDone, onError }: { fact: ProjectFact; canEdit: boolean; onDone: () => void; onError: (m: string) => void }) {
  const [value, setValue] = useState(fact.value);
  const [until, setUntil] = useState(fact.valid_until ?? '');
  const [busy, setBusy] = useState(false);
  async function go(approve: boolean) {
    setBusy(true);
    try { await reviewFact(fact.id, approve, value, until || null); onDone(); } catch (e) { onError(friendlyError(e)); setBusy(false); }
  }
  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="warn">{factCategoryLabel[fact.category]}</Badge>
        <p className="font-medium">{fact.label}</p>
        {fact.source_page && <span className="text-xs text-muted">page {fact.source_page}</span>}
      </div>
      <input aria-label={`Value for ${fact.label}`} value={value} onChange={(e) => setValue(e.target.value)} disabled={!canEdit}
        className="w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-accent" />
      {fact.category === 'offer' && (
        <label className="flex items-center gap-2 text-sm">Ends on
          <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="rounded-lg border border-line px-2 py-1" />
        </label>
      )}
      {canEdit && (
        <div className="flex gap-2">
          <Button variant="primary" disabled={busy} onClick={() => go(true)}>Approve</Button>
          <Button variant="ghost" disabled={busy} onClick={() => go(false)}>Reject</Button>
        </div>
      )}
    </li>
  );
}

function AddFactModal({ projectId, onClose, onSaved }: { projectId: string; onClose: () => void; onSaved: () => void }) {
  const [category, setCategory] = useState<FactCategory>('price');
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try { await addFact(projectId, category, label, value, until || null); onSaved(); } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }
  return (
    <Modal open onClose={onClose} title="Add a fact">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <SelectField label="Kind" value={category} onChange={(e) => setCategory(e.target.value as FactCategory)}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{factCategoryLabel[c]}</option>)}
        </SelectField>
        <TextField label="What it is" required value={label} onChange={(e) => setLabel(e.target.value)} placeholder="3 BHK (1,850 sq ft)" />
        <TextField label="Value, exactly as on the builder's sheet" required value={value} onChange={(e) => setValue(e.target.value)} placeholder="₹1.42 Cr all inclusive" />
        {category === 'offer' && <TextField label="Offer ends on" type="date" required value={until} onChange={(e) => setUntil(e.target.value)} />}
        <p className="text-xs text-muted">You are approving this fact. The AI desk may quote it to buyers straight away.</p>
        <ErrorText>{error}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : 'Add and approve'}</Button>
        </div>
      </form>
    </Modal>
  );
}

function NewSheetModal({ projectId, onClose, onSaved }: { projectId: string; onClose: () => void; onSaved: (n: number) => void }) {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try { onSaved(await startNewPriceSheet(projectId, asOf)); } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }
  return (
    <Modal open onClose={onClose} title="New price sheet">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <p className="text-sm">This retires every current price and payment-plan fact for this project, so the AI stops quoting old prices. Replies waiting for review on this project become stale and can't be sent.</p>
        <TextField label="Date on the new price sheet" type="date" required value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        <ErrorText>{error}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="danger" disabled={busy}>{busy ? 'Working…' : 'Retire old prices'}</Button>
        </div>
      </form>
    </Modal>
  );
}

function RegistrationSettings({ projectId, email, days, onSaved, onError }: { projectId: string; email: string | null; days: number; onSaved: () => Promise<void>; onError: (m: string) => void }) {
  const [e, setE] = useState(email ?? '');
  const [d, setD] = useState(String(days ?? 30));
  const [saved, setSaved] = useState(false);
  const changed = e !== (email ?? '') || d !== String(days ?? 30);
  return (
    <form className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-line bg-surface px-4 py-3"
      onSubmit={async (ev: FormEvent) => {
        ev.preventDefault(); setSaved(false);
        try { await setProjectRegistration(projectId, e, Number(d)); await onSaved(); setSaved(true); } catch (err) { onError(friendlyError(err)); }
      }}>
      <div className="min-w-[220px] flex-1"><TextField label="Builder's email for client registrations" type="email" value={e} onChange={(x) => setE(x.target.value)} placeholder="cp.registrations@builder.com" /></div>
      <div className="w-[160px]"><TextField label="Registration valid (days)" type="number" min={1} max={365} value={d} onChange={(x) => setD(x.target.value)} /></div>
      <Button type="submit" disabled={!changed}>Save</Button>
      {saved && !changed && <span className="text-sm text-ok">Saved</span>}
    </form>
  );
}

function Amenities({ items }: { items: string[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, 10);
  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map((a, i) => <span key={i} className="rounded-full bg-surface2 px-2.5 py-1 text-xs">{a}</span>)}
      {items.length > 10 && <button className="px-1 text-xs text-accent hover:underline" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `+${items.length - 10} more`}</button>}
    </div>
  );
}
